import React, { useEffect, useState } from 'react';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import Footer from '../components/shared/Footer';
import { newsAPI } from '../services/api';
import './News.css';

const ACTION_LABELS = {
  visit: 'Visit',
  read_more: 'Read More',
  facebrowser_post: 'Facebrowser Post',
  online_shop: 'Online Shop',
  careers: 'Careers',
  more_info: 'More Info'
};

const NewsAction = ({ article }) => {
  const actionType = article.action_type || 'read_more';
  const href = article.full_article_url;

  if (actionType === 'none' || !href) {
    return null;
  }

  const label = actionType === 'custom'
    ? article.custom_button_text
    : ACTION_LABELS[actionType] || 'Read More';
  const isInternal = /^\/(?!\/)/.test(href);

  return (
    <a
      href={href}
      {...(!isInternal && { target: '_blank', rel: 'noopener noreferrer' })}
      className="news-action-button"
    >
      {label}
    </a>
  );
};

const News = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        setLoading(true);
        const response = await newsAPI.getPublic();
        if (response.success) {
          setArticles(response.data || []);
        } else {
          setError(response.message || 'Failed to load news');
        }
      } catch (loadError) {
        console.error('Error loading news:', loadError);
        setError(loadError.message || 'Failed to load news');
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, []);

  return (
    <div className="news-page-wrapper">
      <ResponsiveNavbar
        menuItems={[
          { type: 'link', label: 'Veloura', to: '/' },
          { type: 'link', label: 'Shop', to: '/shop' },
          { type: 'link', label: 'Team', to: '/team' },
          { type: 'link', label: 'Careers', to: '/careers' },
          { type: 'link', label: 'Partners', to: '/partners' },
          { type: 'link', label: 'News', to: '/news', active: true },
          { type: 'link', label: 'Account', to: '/account' }
        ]}
        logo="VELOURA"
        logoLink="/"
        scrolled={scrolled}
      />

      <main className="news-page">
        <div className="news-container">
          <header className="news-page-header">
            <span className="news-page-kicker">Veloura Journal</span>
            <h1>Latest News</h1>
            <p className="news-intro">
              Stay updated with announcements, releases, and important updates from Veloura.
            </p>
          </header>

          {loading && (
            <div className="news-empty-state">
              <p>Loading news articles...</p>
            </div>
          )}

          {!loading && error && (
            <div className="news-empty-state">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && (
            <div className="news-list">
              {articles.length === 0 ? (
                <div className="news-empty-state">
                  <p>No news articles are currently published.</p>
                </div>
              ) : (
                articles.map((article) => (
                  <article
                    key={article.id}
                    className={`news-card${article.background_image_url ? ' has-background' : ''}`}
                    style={article.background_image_url ? {
                      backgroundImage: `url(${article.background_image_url})`
                    } : undefined}
                  >
                    <div className="news-card-scrim" aria-hidden="true" />
                    <div className="news-card-inner">
                      <div className="news-card-meta">
                        <span className="news-category">{article.category || 'Company News'}</span>
                        <time className="news-date" dateTime={article.submitted_at}>
                          {new Date(article.submitted_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </time>
                      </div>

                      <h2>{article.title}</h2>
                      <p className="news-description">{article.description || 'No summary provided.'}</p>

                      <div className="news-card-actions">
                        <NewsAction article={article} />
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default News;
